import { useQuery } from "@tanstack/react-query";
import { Moon, Sun } from "lucide-react";
import { MotionConfig } from "motion/react";
import { useEffect } from "react";
import { NavLink, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { api, loginUrl } from "./api/client.js";
import { Logomark } from "./components/logomark.js";
import { Button } from "./components/ui/button.js";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card.js";
import { useTheme } from "./hooks/use-theme.js";
import { cn } from "./lib/utils.js";
import { Dashboard } from "./routes/Dashboard.js";
import { ProjectDetail } from "./routes/ProjectDetail.js";
import { Projects } from "./routes/Projects.js";
import { Rules } from "./routes/Rules.js";
import { Security } from "./routes/Security.js";
import { Settings } from "./routes/Settings.js";
import { ReviewDetail, Reviews } from "./routes/Reviews.js";

const NAV = [
  { to: "/", label: "Übersicht" },
  { to: "/projects", label: "Projekte" },
  { to: "/rules", label: "Regeln" },
  { to: "/reviews", label: "Reviews" },
  { to: "/security", label: "Sicherheit", badge: "secrets" as const },
  { to: "/settings", label: "Einstellungen" },
];

export function App() {
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, retry: false });
  const [theme, setTheme] = useTheme();
  const location = useLocation();
  // The nav badge: open secrets outside any diff. Same query key the Sicherheit
  // screen invalidates, so dismissing a fund drops the count immediately.
  const secrets = useQuery({
    queryKey: ["secrets-outside-diff", "open", "all"],
    queryFn: () => api.secretsOutsideDiff({ status: "open" }),
    enabled: !me.isError,
  });
  const openSecrets = secrets.data?.length ?? 0;

  // The static #boot-splash in index.html paints before any JS runs, so
  // reload never flashes white — this hands off to React's own identical
  // loop the instant it mounts, then fades once (both look the same, so the
  // handoff itself is invisible).
  useEffect(() => {
    const bootSplash = document.getElementById("boot-splash");
    if (!bootSplash) return;
    bootSplash.classList.add("boot-splash--out");
    const timeout = setTimeout(() => bootSplash.remove(), 400);
    return () => clearTimeout(timeout);
  }, []);

  if (me.isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Logomark size="lg" loop />
      </div>
    );
  }

  return (
    <MotionConfig reducedMotion="user">
      <div className="app-boot-in min-h-screen bg-background text-foreground">
        <Toaster position="bottom-right" richColors />
        {/* Clean white header: wordmark left, pill-hover nav, hairline below. */}
        <header className="sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <div className="mx-auto flex h-16 max-w-[1200px] items-center gap-4 px-4 sm:gap-8 sm:px-6">
            <Logomark />
            {/* On narrow screens the nav scrolls; the mask fades the clipped
                edge so a half-visible pill reads as "more", not as broken. */}
            <nav className="flex items-center gap-1 overflow-x-auto max-sm:[mask-image:linear-gradient(90deg,#000_calc(100%-28px),transparent)]">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    cn(
                      "rounded-full px-4 py-2 text-sm transition-colors",
                      isActive
                        ? "bg-foreground font-semibold text-background"
                        : "font-medium text-muted-foreground hover:bg-muted hover:text-foreground",
                    )
                  }
                >
                  {item.label}
                  {/* Only ever the open funds — a zero badge is no badge. Magenta,
                      like every other "look here" in this UI; on the active pill
                      the ink ground already isolates it. */}
                  {item.badge === "secrets" && openSecrets ? (
                    <span
                      className="ml-1.5 inline-flex min-w-5 items-center justify-center rounded-full bg-primary px-1.5 py-0.5 text-xs font-bold tabular-nums text-primary-foreground"
                      title={`${openSecrets} offene Funde außerhalb eines Diffs`}
                    >
                      {openSecrets}
                    </span>
                  ) : null}
                </NavLink>
              ))}
            </nav>
            <span className="ml-auto flex items-center gap-2">
              {me.data?.dev ? (
                <span
                  className="whitespace-nowrap rounded-full bg-muted px-2.5 py-0.5 text-xs font-semibold text-muted-foreground"
                  title="Dev-Modus: Anmeldung umgangen, Daten aus dem Demo-Seed"
                >
                  Demo
                </span>
              ) : null}
              <span className="hidden text-sm text-muted-foreground md:inline">
                {me.data?.username}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9"
                aria-label={theme === "dark" ? "Helles Design verwenden" : "Dunkles Design verwenden"}
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              >
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
            </span>
          </div>
        </header>

        {me.isError ? (
          <main className="flex min-h-[calc(100vh-4rem)] items-center justify-center p-6">
            <Card className="w-full max-w-sm">
              <CardHeader>
                <CardTitle>Anmeldung erforderlich</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Diese Ablage verlangt eine Anmeldung über GitLab, bevor Projekte, Regeln und
                  Reviews gelesen werden können.
                </p>
                <Button asChild className="w-full">
                  <a href={loginUrl(location.pathname + location.search)}>Mit GitLab anmelden</a>
                </Button>
              </CardContent>
            </Card>
          </main>
        ) : (
          <main className="mx-auto max-w-[1200px] px-6 py-8 pb-16">
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/projects/:id" element={<ProjectDetail />} />
              <Route path="/rules" element={<Rules />} />
              <Route path="/reviews" element={<Reviews />} />
              <Route path="/reviews/:id" element={<ReviewDetail />} />
              <Route path="/security" element={<Security />} />
              <Route path="/settings" element={<Settings />} />
            </Routes>
          </main>
        )}
      </div>
    </MotionConfig>
  );
}
